import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal, Stack, Group, Text, NumberInput,
  Slider, Button, Badge, MultiSelect, JsonInput, Divider, Textarea,
} from '@mantine/core';
import { saveAgent, selectAgentsSaving } from '../../redux/slices/agentsSlice';

const AVAILABLE_TOOLS = [
  { value: 'search_model_card', label: 'Search Model Card' },
  { value: 'search_risk_catalogues', label: 'Search Risk Catalogues' },
  { value: 'search_governance_policies', label: 'Search Governance Policies' },
  { value: 'search_security_policies', label: 'Search Security Policies' },
  { value: 'search_ai_policies', label: 'Search AI Policies' },
  { value: 'get_assessment_state', label: 'Get Assessment State' },
  { value: 'evaluate_output', label: 'Evaluate Output Quality' },
  { value: 'delegate', label: 'Delegate (Orchestrator)' },
  { value: 'check_progress', label: 'Check Progress' },
  { value: 'evaluate_completeness', label: 'Evaluate Completeness' },
];

export default function AgentEditorModal({ agent, opened, onClose }) {
  const dispatch = useDispatch();
  const saving = useSelector(selectAgentsSaving);

  const [tools, setTools] = useState([]);
  const [maxIterations, setMaxIterations] = useState(3);
  const [qualityThreshold, setQualityThreshold] = useState(0.7);
  const [decisionCriteria, setDecisionCriteria] = useState('{}');
  const [agentInstructions, setAgentInstructions] = useState('');

  useEffect(() => {
    if (agent) {
      setTools(agent.tools || []);
      setMaxIterations(agent.max_iterations || 3);
      setQualityThreshold(agent.quality_threshold || 0.7);
      setDecisionCriteria(JSON.stringify(agent.decision_criteria || {}, null, 2));
      setAgentInstructions(agent.agent_instructions || '');
    }
  }, [agent]);

  const handleSave = async () => {
    let parsedCriteria = {};
    try {
      parsedCriteria = JSON.parse(decisionCriteria);
    } catch {
      // Keep existing
    }

    await dispatch(saveAgent({
      agent_key: agent.agent_key,
      tools,
      max_iterations: maxIterations,
      quality_threshold: qualityThreshold,
      decision_criteria: parsedCriteria,
      agent_instructions: agentInstructions,
    }));

    onClose();
  };

  if (!agent) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Agent: ${agent.agent_name}`}
      size="lg"
    >
      <Stack gap="md">
        <Group gap="xs">
          <Badge color="blue" variant="light">{agent.agent_category}</Badge>
          <Badge color="gray" variant="light">v{agent.version || 1}</Badge>
        </Group>

        <Text size="sm" c="dimmed">{agent.goal}</Text>

        <Divider label="Prompts" labelPosition="left" />
        <Group gap="xs" wrap="wrap">
          {(agent.prompt_keys || []).map(key => (
            <Badge key={key} variant="outline" size="sm">{key}</Badge>
          ))}
          {!agent.prompt_keys?.length && <Text size="xs" c="dimmed">No prompts assigned</Text>}
        </Group>

        <Divider label="Tools" labelPosition="left" />
        <MultiSelect
          data={AVAILABLE_TOOLS}
          value={tools}
          onChange={setTools}
          placeholder="Select tools for this agent"
          searchable
        />

        <Divider label="Quality Settings" labelPosition="left" />
        <NumberInput
          label="Max Iterations"
          description="Maximum retry attempts per prompt when quality threshold is not met"
          value={maxIterations}
          onChange={setMaxIterations}
          min={1}
          max={10}
        />

        <Stack gap={4}>
          <Text size="sm" fw={500}>Quality Threshold: {(qualityThreshold * 100).toFixed(0)}%</Text>
          <Text size="xs" c="dimmed">Minimum quality score to accept output without retrying</Text>
          <Slider
            value={qualityThreshold * 100}
            onChange={(v) => setQualityThreshold(v / 100)}
            min={0}
            max={100}
            step={5}
            marks={[
              { value: 0, label: '0%' },
              { value: 50, label: '50%' },
              { value: 70, label: '70%' },
              { value: 100, label: '100%' },
            ]}
          />
        </Stack>

        <Divider label="Decision Criteria" labelPosition="left" />
        <JsonInput
          label="Decision Criteria (JSON)"
          description="Custom criteria the agent uses to evaluate its outputs"
          value={decisionCriteria}
          onChange={setDecisionCriteria}
          formatOnBlur
          autosize
          minRows={3}
          maxRows={8}
        />

        <Divider label="Instructions" labelPosition="left" />
        <Textarea
          label="Agent Instructions (Markdown)"
          description="Custom directives appended to the agent's system prompt at runtime"
          value={agentInstructions}
          onChange={(e) => setAgentInstructions(e.currentTarget.value)}
          placeholder={'## Focus Areas\n- Prioritize regulatory compliance risks\n\n## Constraints\n- Maximum 5 risks per assessment area'}
          autosize
          minRows={4}
          maxRows={12}
          styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>Save Changes</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
